import { supabase } from './supabase'
import { runQuery } from './sqlite-engine'
import normalizeSearchInput from './normalize-search-input'
import type { SearchResult } from '../types/dictionary'

type OfflineRow = {
  id: number
  json_data: string | SearchResult['json_data']
  frequency: number
  radical: number | null
  hsk: number | null
  tocfl: number | null
  exact_simp: string
  exact_trad: string
  pinyin_display: string
  zhuyin_display: string
  // Translation data (embedded in SQLite)
  definitions?: string
  examples?: string
  // Ranking helpers returned by SQL
  rank_exact?: number
  rank_prefix?: number
}

// --- OFFLINE SEARCH ---
export async function searchOffline(
  input: string,
  englishMode: boolean,
  page: number = 0,
  limit: number = 50,
  radical?: number | null
): Promise<SearchResult[]> {
  const { raw, clean, num } = normalizeSearchInput(input)
  if (!raw) return []

  const offset = page * limit

  let queryStr = ''
  let ftsTarget = ''
  let orderByClause = ''

  if (englishMode) {
    ftsTarget = 'definition_tokens'
    const tokens = clean.split(/\s+/).filter(Boolean)
    queryStr = tokens.map((t) => `"${t}"*`).join(' AND ')
    orderByClause = `ORDER BY length(s.definition_tokens) ASC, e.frequency ASC`
  } else {
    // Detect Input Type
    const isZhuyin = /[\u3100-\u312F]/.test(raw)
    const hasNumbers = /\d/.test(raw)
    const isPinyin = !isZhuyin && /^[a-zA-Z0-9\s]+$/.test(raw)

    if (isZhuyin) {
      ftsTarget = 'zhuyin_tokens'
      const tokens = raw.split(/\s+/).filter(Boolean)
      queryStr = tokens.map((t) => `"${t}"*`).join(' AND ')
    } else if (isPinyin) {
      if (hasNumbers) {
        // STRICT MODE: User typed numbers (e.g. "yi2 si4")
        // Target ONLY the numbered column.
        // We use 'raw' to tokenize by space: "yi2 si4" -> MATCH "yi2"* AND "si4"*
        ftsTarget = 'pinyin_num_tokens'
        const tokens = raw.toLowerCase().split(/\s+/).filter(Boolean)
        queryStr = tokens.map((t) => `"${t}"*`).join(' AND ')
      } else {
        // BROAD MODE: No numbers. Target clean column.
        ftsTarget = 'pinyin_clean_tokens'
        const tokens = clean.split(/\s+/).filter(Boolean)
        queryStr = tokens.map((t) => `"${t}"*`).join(' AND ')
      }
    } else {
      // Hanzi - search both simplified and traditional columns
      // FTS5 multi-column search: use column filters in query string
      ftsTarget = '' // Empty because we'll search with column filters in query string
      const chars = raw.split('').join(' ')
      // Use column filters: search both simp_tokens and trad_tokens
      queryStr = `simp_tokens:"${chars}" OR trad_tokens:"${chars}"`
    }
    orderByClause = `ORDER BY score DESC, length(e.exact_simp) ASC, e.frequency ASC`
  }

  if (!queryStr) return []

  // SQL Query
  const sql = `
    SELECT 
      e.id, e.json_data, e.frequency, e.radical, e.hsk, e.tocfl,
      e.exact_simp, e.exact_trad, 
      e.pinyin_display, e.zhuyin_display,
      e.definitions, e.examples,
      
      (CASE 
        -- Exact Matches
        WHEN e.exact_simp = ? OR e.exact_trad = ? THEN 100
        WHEN e.exact_pinyin_num = ? THEN 98
        WHEN e.exact_pinyin_clean = ? THEN 90
        
        -- Prefix Matches
        WHEN e.exact_trad LIKE ? || '%' THEN 85
        WHEN e.exact_simp LIKE ? || '%' THEN 85
        WHEN e.exact_pinyin_num LIKE ? || '%' THEN 80
        WHEN e.exact_pinyin_clean LIKE ? || '%' THEN 70
        
        -- Infix Matches (Matches logic in Supabase)
        -- Note: FTS filter ensures it matches, this just assigns the score.
        WHEN e.exact_pinyin_num LIKE '%' || ? || '%' THEN 60
        
        ELSE 50
      END) as score

    FROM search_index s
    JOIN entries e ON e.id = s.rowid
    WHERE ${ftsTarget ? `s.${ftsTarget}` : 'search_index'} MATCH ?
    ${radical !== null && radical !== undefined ? 'AND e.radical = ?' : ''}
    ${orderByClause} 
    LIMIT ? OFFSET ?
  `

  const params = [
    raw,
    raw,
    num,
    clean,
    raw,
    raw,
    num,
    clean,
    num,
    queryStr,
    ...(radical !== null && radical !== undefined ? [radical] : []),
    limit,
    offset
  ]

  const rows = (await runQuery(sql, params)) as OfflineRow[]
  return rows.map(mapRowToResult)
}

export async function searchExact(
  text: string,
  scriptType: 'simplified' | 'traditional',
  isOffline: boolean
): Promise<SearchResult[]> {
  if (!text.trim()) return []

  // Detect if input contains hanzi characters (Unicode range \u4E00-\u9FFF)
  const isHanzi = /[\u4E00-\u9FFF]/.test(text)

  // If hanzi, search both columns; otherwise use the specified scriptType
  if (isHanzi) {
    // Search both simplified and traditional columns
    if (isOffline) {
      const sql = `SELECT * FROM entries WHERE exact_simp = ? OR exact_trad = ? ORDER BY frequency ASC`
      const rows = await runQuery(sql, [text, text])
      return rows.map(mapRowToResult)
    } else {
      // Query both columns with translations join and merge results
      const selectWithTranslations = `
        *,
        entry_translations!left (
          locale,
          definitions,
          examples
        )
      `
      const [simpData, tradData] = await Promise.all([
        supabase
          .from('dict_entries')
          .select(selectWithTranslations)
          .eq('simplified', text)
          .eq('entry_translations.locale', 'en')
          .order('frequency', { ascending: true }),
        supabase
          .from('dict_entries')
          .select(selectWithTranslations)
          .eq('traditional', text)
          .eq('entry_translations.locale', 'en')
          .order('frequency', { ascending: true })
      ])

      if (simpData.error) throw simpData.error
      if (tradData.error) throw tradData.error

      // Merge results and deduplicate by id
      const allResults = [...(simpData.data || []), ...(tradData.data || [])]
      const uniqueResults = Array.from(
        new Map(allResults.map((item) => [item.id, item])).values()
      )
      // Sort by frequency
      uniqueResults.sort((a, b) => (a.frequency || 0) - (b.frequency || 0))

      return uniqueResults.map(mapRowToResult)
    }
  } else {
    // Non-hanzi: search only the specified column (existing behavior)
    const column = scriptType === 'traditional' ? 'traditional' : 'simplified'
    const dbCol = scriptType === 'traditional' ? 'exact_trad' : 'exact_simp'

    if (isOffline) {
      const sql = `SELECT * FROM entries WHERE ${dbCol} = ? ORDER BY frequency ASC`
      const rows = await runQuery(sql, [text])
      return rows.map(mapRowToResult)
    } else {
      const { data, error } = await supabase
        .from('dict_entries')
        .select(
          `
          *,
          entry_translations!left (
            locale,
            definitions,
            examples
          )
        `
        )
        .eq(column, text)
        .eq('entry_translations.locale', 'en')
        .order('frequency', { ascending: true })

      if (error) throw error
      return (data || []).map(mapRowToResult)
    }
  }
}

// --- ONLINE SEARCH (Supabase) ---
export async function searchOnline(
  input: string,
  englishMode: boolean,
  page: number = 0,
  limit: number = 50,
  radical?: number | null,
  locale: string = 'en'
): Promise<SearchResult[]> {
  const { raw, clean, num } = normalizeSearchInput(input)
  if (!raw) return []

  const { data, error } = await supabase.rpc('search_entries', {
    raw_input: raw,
    clean_input: clean,
    num_input: num,
    english_mode: englishMode,
    page_number: page,
    page_size: limit,
    radical_filter: radical ?? null,
    locale
  })

  if (error) throw error
  const results = (data as SearchResult[]) || []
  return results
}

// --- COMPONENT LOOKUP ---
export async function getComponents(
  chars: string[],
  isOffline: boolean,
  isTraditionalMode: boolean
): Promise<SearchResult[]> {
  if (chars.length === 0) return []

  if (isOffline) {
    // Use IN clause on the main table for O(1) lookups
    const col = isTraditionalMode ? 'exact_trad' : 'exact_simp'
    const placeholders = chars.map(() => '?').join(',')

    const sql = `SELECT * FROM entries WHERE ${col} IN (${placeholders})`
    const rows = await runQuery(sql, chars)

    return rows.map(mapRowToResult)
  } else {
    const col = isTraditionalMode ? 'traditional' : 'simplified'
    const { data } = await supabase
      .from('dict_entries')
      .select(
        `
        *,
        entry_translations!left (
          locale,
          definitions,
          examples
        )
      `
      )
      .eq('entry_translations.locale', 'en')
      .in(col, chars)
    return ((data as unknown[]) || []).map(mapRowToResult)
  }
}

// --- ID LOOKUP ---
export async function getEntryById(
  id: number,
  isOffline: boolean
): Promise<SearchResult | null> {
  if (isOffline) {
    const sql = `SELECT * FROM entries WHERE id = ?`
    const rows = await runQuery(sql, [id])
    return rows.length > 0 ? mapRowToResult(rows[0]) : null
  } else {
    const { data } = await supabase
      .from('dict_entries')
      .select(
        `
        *,
        entry_translations!left (
          locale,
          definitions,
          examples
        )
      `
      )
      .eq('id', id)
      .eq('entry_translations.locale', 'en')
      .single()
    return data ? mapRowToResult(data) : null
  }
}

// Helper: Map database row to SearchResult
export const mapRowToResult = (row: unknown): SearchResult => {
  const record = row as Record<string, unknown>
  const json =
    typeof record.json_data === 'string'
      ? JSON.parse(record.json_data)
      : (record.json_data as SearchResult['json_data'])

  // Extract definitions - can come from different sources:
  // 1. Direct from RPC response (definitions field as array)
  // 2. From SQLite (definitions as JSON string)
  // 3. From Supabase embedded select (entry_translations object/array)
  let definitions: string[] = []
  let examples: SearchResult['examples'] = []

  // Check for nested entry_translations from Supabase embedded select
  const translations = record.entry_translations as
    | { definitions?: string[]; examples?: unknown[] }
    | { definitions?: string[]; examples?: unknown[] }[]
    | null
    | undefined

  if (translations) {
    // entry_translations can be an array (from LEFT JOIN) or single object
    const trans = Array.isArray(translations) ? translations[0] : translations
    if (trans) {
      if (Array.isArray(trans.definitions)) {
        definitions = trans.definitions
      }
      if (Array.isArray(trans.examples)) {
        examples = trans.examples as SearchResult['examples']
      }
    }
  }

  // Fallback: Check direct fields (from RPC or flat response)
  if (definitions.length === 0) {
    if (Array.isArray(record.definitions)) {
      definitions = record.definitions as string[]
    } else if (typeof record.definitions === 'string') {
      try {
        definitions = JSON.parse(record.definitions)
      } catch {
        definitions = []
      }
    }
  }

  // Fallback for examples
  if (!examples || examples.length === 0) {
    if (Array.isArray(record.examples)) {
      examples = record.examples as SearchResult['examples']
    } else if (typeof record.examples === 'string') {
      try {
        examples = JSON.parse(record.examples)
      } catch {
        examples = []
      }
    }
  }

  const preview = definitions[0] || (record.preview as string) || ''

  return {
    id: record.id as number,
    simplified: (record.simplified as string) ?? (record.exact_simp as string),
    traditional:
      (record.traditional as string) ??
      (record.exact_trad as string) ??
      (record.simplified as string) ??
      (record.exact_simp as string),
    pinyin:
      (record.pinyin_display as string) ?? (record.pinyin as string) ?? '',
    zhuyin:
      (record.zhuyin_display as string) ?? (record.zhuyin as string) ?? '',
    preview,
    json_data: json,
    score: (record.score as number) || 0,
    frequency: record.frequency as number,
    radical: (record.radical as number) ?? null,
    hsk: (record.hsk as number) ?? null,
    tocfl: (record.tocfl as number) ?? null,
    definitions,
    examples
  }
}
